const pageStyle = {
  maxWidth: 800,
  margin: "0 auto",
  padding: "40px 20px",
  color: "var(--text)",
  lineHeight: 1.7,
  fontSize: 15,
};

const sectionStyle = {
  marginBottom: 40,
};

const h1Style = {
  fontSize: "2em",
  fontWeight: 700,
  marginBottom: 8,
};

const h2Style = {
  fontSize: "1.4em",
  fontWeight: 600,
  marginTop: 32,
  marginBottom: 12,
  color: "var(--text)",
};

const lastUpdated = "April 12, 2026";

const policyNavStyle = {
  display: "flex",
  gap: 24,
  padding: "0 0 24px",
  borderBottom: "1px solid var(--border)",
  marginBottom: 20,
  flexWrap: "wrap",
  fontSize: 14,
};

const policyNavLinkStyle = {
  color: "var(--primary)",
  textDecoration: "none",
  fontWeight: 500,
};

const linkStyle = { color: "var(--primary)" };

const lastUpdatedStyle = { opacity: 0.7, fontSize: 13 };

const PolicyNav = () => (
  <nav style={policyNavStyle}>
    <a href="/terms" style={policyNavLinkStyle}>Terms of Service</a>
    <a href="/privacy" style={policyNavLinkStyle}>Privacy Policy</a>
    <a href="/refund-policy" style={policyNavLinkStyle}>Refund Policy</a>
    <a href="/" style={{ ...policyNavLinkStyle, opacity: 0.7 }}>
      &larr; Back to Home
    </a>
  </nav>
);

export const TermsPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Terms of Service</h1>
      <p style={lastUpdatedStyle}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>1. Acceptance of Terms</h2>
      <p>
        By accessing or using Supergreen ("the Service"), you agree to be bound
        by these Terms of Service ("Terms"). If you do not agree to all of these
        Terms, you must not use the Service. Your continued use of the Service
        following any changes to these Terms constitutes acceptance of those
        changes.
      </p>

      <h2 style={h2Style}>2. Description of Service</h2>
      <p>
        Supergreen provides an API-based platform that allows users to connect
        their WhatsApp and Telegram accounts for automation purposes. The
        Service enables sending and receiving messages, managing conversations,
        and integrating messaging workflows via a RESTful API. The Service is
        provided on an "as is" basis and its functionality may depend on
        third-party platforms and services outside of our control.
      </p>

      <h2 style={h2Style}>3. Account Registration</h2>
      <p>
        You must provide a valid email address to create an account. You are
        responsible for maintaining the confidentiality of your account
        credentials, API tokens, and secret tokens. You are responsible for all
        activities that occur under your account. You must notify us immediately
        of any unauthorized use of your account or any other breach of security.
      </p>

      <h2 style={h2Style}>4. Acceptable Use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>
          Send spam, unsolicited messages, or bulk commercial communications
        </li>
        <li>Violate any applicable laws or regulations</li>
        <li>
          Violate the terms of service of third-party platforms (WhatsApp,
          Telegram, etc.)
        </li>
        <li>Harass, abuse, threaten, or harm others</li>
        <li>Distribute malware or engage in phishing</li>
        <li>Impersonate any person or entity</li>
        <li>
          Interfere with or disrupt the Service or servers connected to the
          Service
        </li>
        <li>
          Use the Service to transmit content that is illegal, defamatory,
          obscene, or otherwise objectionable
        </li>
        <li>
          Attempt to reverse-engineer, decompile, or extract the source code of
          the Service
        </li>
        <li>
          Resell, sublicense, or redistribute access to the Service without
          prior written consent
        </li>
      </ul>
      <p>
        We reserve the right to determine, in our sole discretion, whether your
        use violates these terms, and to take action including suspension or
        termination of your account without prior notice.
      </p>

      <h2 style={h2Style}>5. Third-Party Platform Disclaimer</h2>
      <p>
        The Service integrates with third-party messaging platforms including,
        but not limited to, WhatsApp (operated by Meta Platforms, Inc.) and
        Telegram (operated by Telegram FZ-LLC). These platforms are independent
        services with their own terms of service, acceptable use policies, and
        enforcement mechanisms.
      </p>
      <p>
        <strong>
          Supergreen is not responsible for any actions taken by third-party
          platforms against your account, including but not limited to:
        </strong>
      </p>
      <ul>
        <li>
          Temporary or permanent bans, suspensions, or restrictions imposed on
          your WhatsApp, Telegram, or other messaging accounts
        </li>
        <li>
          Loss of access to your phone number, contacts, message history, or
          other data held by the third-party platform
        </li>
        <li>
          Changes to third-party platform APIs, terms of service, or policies
          that affect the functionality of the Service
        </li>
        <li>
          Rate limiting, throttling, or blocking of messages by the third-party
          platform
        </li>
        <li>
          Any other adverse action taken by the third-party platform as a result
          of your use of the Service or otherwise
        </li>
      </ul>
      <p>
        You acknowledge that the use of automation tools with messaging
        platforms may carry inherent risks, including the risk of account
        restrictions or bans by the platform operator. You assume full
        responsibility for your use of the Service and for compliance with the
        terms of service of all third-party platforms you connect. Supergreen
        makes no guarantees regarding the continued availability or
        compatibility of any third-party platform integration.
      </p>

      <h2 style={h2Style}>6. User Responsibilities</h2>
      <p>
        You are solely responsible for the content of all messages sent through
        the Service and for ensuring that your use of the Service complies with
        all applicable laws and regulations, including anti-spam laws, data
        protection regulations, and the terms of service of connected
        third-party platforms. You must obtain all necessary consents from
        message recipients before using the Service to contact them.
      </p>

      <h2 style={h2Style}>7. Pricing and Payment</h2>
      <p>
        The Service is billed at $10 per connected line per month. A free trial
        period is available for new users. Payments are processed through our
        payment partner, Stripe. All fees are non-refundable except as described
        in our{" "}
        <a href="/refund-policy" style={linkStyle}>
          Refund Policy
        </a>. We reserve the right to change pricing with 30 days notice to
        existing subscribers.
      </p>

      <h2 style={h2Style}>8. Free Trial</h2>
      <p>
        New users may be eligible for a free trial period. At the end of the
        trial, your account will be billed at the standard rate unless you
        cancel before the trial ends. Trial eligibility is limited to one per
        user. We reserve the right to modify or discontinue the free trial at
        any time.
      </p>

      <h2 style={h2Style}>9. Cancellation</h2>
      <p>
        You may cancel your subscription at any time. Upon cancellation, your
        access will continue until the end of the current billing period. After
        that, connected lines will be disconnected and your data will be handled
        in accordance with our{" "}
        <a href="/privacy" style={linkStyle}>
          Privacy Policy
        </a>.
      </p>

      <h2 style={h2Style}>10. Intellectual Property</h2>
      <p>
        The Service, including its software, design, documentation, APIs, and
        all related intellectual property, is owned by Supergreen and protected
        by applicable intellectual property laws. You retain ownership of any
        data you transmit through the Service. You are granted a limited,
        non-exclusive, non-transferable, revocable license to use the Service
        for its intended purpose in accordance with these Terms.
      </p>

      <h2 style={h2Style}>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Supergreen and its owners,
        operators, employees, and affiliates shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages, or
        any loss of profits, revenues, data, or business opportunities, whether
        incurred directly or indirectly, arising from your use of or inability
        to use the Service. This includes, without limitation, damages arising
        from:
      </p>
      <ul>
        <li>
          Third-party platform actions (bans, suspensions, restrictions)
        </li>
        <li>Service interruptions, downtime, or data loss</li>
        <li>Unauthorized access to your account</li>
        <li>
          Any content or messages sent or received through the Service
        </li>
      </ul>
      <p>
        The Service is provided "as is" and "as available" without warranties of
        any kind, whether express or implied, including but not limited to
        implied warranties of merchantability, fitness for a particular purpose,
        and non-infringement. Our total liability to you for any claims arising
        from or related to the Service shall not exceed the amount you paid us
        in the 3 months preceding the claim.
      </p>

      <h2 style={h2Style}>12. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Supergreen, its
        owners, operators, employees, and affiliates from and against any and
        all claims, liabilities, damages, losses, costs, and expenses (including
        reasonable legal fees) arising from or related to: (a) your use of the
        Service; (b) your violation of these Terms; (c) your violation of any
        third-party rights, including third-party platform terms of service; or
        (d) any content or messages you transmit through the Service.
      </p>

      <h2 style={h2Style}>13. Service Availability</h2>
      <p>
        We strive to maintain high availability but do not guarantee
        uninterrupted or error-free service. The Service depends on third-party
        platforms (WhatsApp, Telegram) and infrastructure providers whose
        availability is outside our control. We may perform scheduled or
        unscheduled maintenance that temporarily affects service availability.
        We are not liable for downtime caused by third-party services,
        infrastructure failures, or force majeure events.
      </p>

      <h2 style={h2Style}>14. Modifications to Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify users of
        material changes via email or through the Service. Continued use of the
        Service after changes are posted constitutes acceptance of the updated
        Terms. If you do not agree with the updated Terms, you must stop using
        the Service.
      </p>

      <h2 style={h2Style}>15. Termination</h2>
      <p>
        We may suspend or terminate your access if you violate these Terms or
        engage in activity harmful to the Service or its users, with or without
        notice. Upon termination, your right to use the Service ceases
        immediately. Provisions of these Terms that by their nature should
        survive termination shall survive, including but not limited to Sections
        5 (Third-Party Platform Disclaimer), 11 (Limitation of Liability), 12
        (Indemnification), and 16 (Governing Law).
      </p>

      <h2 style={h2Style}>16. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with
        applicable law. Any disputes arising from or related to these Terms or
        the Service shall be resolved through good-faith negotiation. If
        negotiation fails, disputes shall be submitted to the competent courts
        of the jurisdiction in which Supergreen operates.
      </p>

      <h2 style={h2Style}>17. Severability</h2>
      <p>
        If any provision of these Terms is found to be invalid or unenforceable,
        the remaining provisions shall continue in full force and effect.
      </p>

      <h2 style={h2Style}>18. Contact</h2>
      <p>
        For questions about these Terms, contact us at{" "}
        <a href="mailto:support@supergreen.cc" style={linkStyle}>
          support@supergreen.cc
        </a>.
      </p>
    </div>
  </div>
);

export const PrivacyPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Privacy Policy</h1>
      <p style={lastUpdatedStyle}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>1. Information We Collect</h2>
      <p>We collect the following types of information:</p>
      <ul>
        <li>
          <strong>Account information:</strong>{" "}
          email address and WhatsApp/Telegram phone numbers you provide when
          registering
        </li>
        <li>
          <strong>Usage data:</strong>{" "}
          API call logs, connection status, timestamps of activity, and error
          logs for debugging purposes
        </li>
        <li>
          <strong>Message data:</strong>{" "}
          messages sent and received through the Service are processed to
          deliver the Service functionality. We do not read or analyze message
          content for advertising or profiling purposes.
        </li>
        <li>
          <strong>Payment information:</strong>{" "}
          payment details are handled by our payment processor, Stripe. We do
          not store credit card numbers on our servers.
        </li>
        <li>
          <strong>Device and connection data:</strong>{" "}
          IP addresses, browser type, and device identifiers used to access the
          Service, collected for security and fraud prevention purposes
        </li>
      </ul>

      <h2 style={h2Style}>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Process payments and manage your subscription</li>
        <li>Send important service notifications and alerts</li>
        <li>Respond to support requests</li>
        <li>Monitor, diagnose, and fix technical issues</li>
        <li>Prevent fraud, abuse, and violations of our Terms of Service</li>
        <li>
          Comply with legal obligations and respond to lawful requests
        </li>
      </ul>

      <h2 style={h2Style}>3. Data Sharing</h2>
      <p>
        We do not sell your personal information. We may share data with:
      </p>
      <ul>
        <li>
          <strong>Payment processors:</strong>{" "}
          Stripe, to process your payments and manage billing
        </li>
        <li>
          <strong>Infrastructure providers:</strong>{" "}
          cloud hosting services (including Hetzner and Google Cloud) necessary
          to operate the Service
        </li>
        <li>
          <strong>Third-party messaging platforms:</strong>{" "}
          WhatsApp and Telegram, as necessary to provide the Service
          functionality. Your use of these platforms is subject to their own
          privacy policies.
        </li>
        <li>
          <strong>Legal requirements:</strong>{" "}
          if required by law, court order, or governmental request, or to
          protect our rights, safety, or property
        </li>
        <li>
          <strong>Business transfers:</strong>{" "}
          in connection with a merger, acquisition, or sale of assets, your data
          may be transferred to the successor entity
        </li>
      </ul>

      <h2 style={h2Style}>4. Data Retention</h2>
      <p>
        We retain your data for as long as your account is active or as needed
        to provide the Service. Upon account deletion, we will remove your
        personal data within 30 days. Some data may be retained longer if
        required by law or for legitimate business purposes such as billing
        records, fraud prevention, or dispute resolution. Aggregated, anonymized
        data that cannot identify you may be retained indefinitely for
        analytical purposes.
      </p>

      <h2 style={h2Style}>5. Data Security</h2>
      <p>
        We use industry-standard security measures to protect your data,
        including encrypted connections (TLS/HTTPS), secure authentication
        mechanisms, access controls, and regular security monitoring. However,
        no method of electronic transmission or storage is 100% secure. In the
        event of a data breach that affects your personal information, we will
        notify you within 72 hours of becoming aware of the breach, as required
        by applicable law.
      </p>

      <h2 style={h2Style}>6. International Data Processing</h2>
      <p>
        The Service operates using infrastructure located in various
        jurisdictions, including the European Union and the United States. By
        using the Service, you consent to the transfer and processing of your
        data in these jurisdictions, which may have different data protection
        laws than your country of residence.
      </p>

      <h2 style={h2Style}>7. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have the right to:
      </p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Object to or restrict certain types of processing</li>
        <li>
          Withdraw consent at any time where processing is based on consent
        </li>
      </ul>
      <p>
        To exercise these rights, contact us at{" "}
        <a href="mailto:support@supergreen.cc" style={linkStyle}>
          support@supergreen.cc
        </a>. We will respond to your request within 30 days.
      </p>

      <h2 style={h2Style}>8. Cookies and Local Storage</h2>
      <p>
        We use essential cookies and local storage for authentication and
        session management. We do not use tracking cookies or third-party
        advertising cookies. You can manage cookie settings through your
        browser, but disabling essential cookies may affect your ability to use
        the Service.
      </p>

      <h2 style={h2Style}>9. Third-Party Services</h2>
      <p>
        The Service integrates with WhatsApp and Telegram. Your use of those
        platforms is governed by their respective privacy policies. We encourage
        you to review them. We are not responsible for the privacy practices of
        third-party platforms.
      </p>

      <h2 style={h2Style}>10. Children's Privacy</h2>
      <p>
        The Service is not intended for children under 18. We do not knowingly
        collect personal information from children. If we become aware that we
        have collected personal data from a child under 18, we will take steps
        to delete that information promptly. If you believe we may have
        collected data from a minor, please contact us at{" "}
        <a href="mailto:support@supergreen.cc" style={linkStyle}>
          support@supergreen.cc
        </a>.
      </p>

      <h2 style={h2Style}>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you
        of material changes via email or through the Service. Your continued use
        of the Service after changes are posted constitutes acceptance of the
        updated policy. We encourage you to review this policy periodically.
      </p>

      <h2 style={h2Style}>12. Contact</h2>
      <p>
        For privacy-related inquiries, contact us at{" "}
        <a href="mailto:support@supergreen.cc" style={linkStyle}>
          support@supergreen.cc
        </a>.
      </p>
    </div>
  </div>
);

export const RefundPolicyPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <div style={sectionStyle}>
      <h1 style={h1Style}>Refund Policy</h1>
      <p style={lastUpdatedStyle}>Last updated: {lastUpdated}</p>

      <h2 style={h2Style}>Eligibility</h2>
      <p>
        If you are not satisfied with the Service, you may request a full refund
        within 14 days of your initial purchase. This applies to the first
        payment only.
      </p>

      <h2 style={h2Style}>How to Request a Refund</h2>
      <p>
        To request a refund, contact us at{" "}
        <a href="mailto:support@supergreen.cc" style={linkStyle}>
          support@supergreen.cc
        </a>{" "}
        with your account email and the reason for the refund. We will process
        eligible refunds within 5-10 business days.
      </p>

      <h2 style={h2Style}>After 14 Days</h2>
      <p>
        After the 14-day refund window, payments are non-refundable. You may
        cancel your subscription at any time, and your access will continue
        until the end of the current billing period.
      </p>

      <h2 style={h2Style}>Third-Party Platform Actions</h2>
      <p>
        Refunds are not available for service disruptions caused by third-party
        platform actions, including but not limited to account bans,
        suspensions, or restrictions imposed by WhatsApp, Telegram, or other
        connected platforms. See our{" "}
        <a href="/terms" style={linkStyle}>
          Terms of Service
        </a>{" "}
        for details on third-party platform disclaimers.
      </p>

      <h2 style={h2Style}>Free Trial</h2>
      <p>
        If you cancel during the free trial period, you will not be charged. No
        refund is needed as no payment has been collected.
      </p>

      <h2 style={h2Style}>Processing</h2>
      <p>
        Refunds are processed through Stripe, our payment provider. The refund
        will be returned to the original payment method. Processing times may
        vary depending on your bank or payment provider.
      </p>
    </div>
  </div>
);

export const LegalPage = () => (
  <div style={pageStyle}>
    <PolicyNav />
    <p>
      Please see our individual policies:
    </p>
    <ul style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <li>
        <a href="/terms" style={linkStyle}>
          Terms of Service
        </a>
      </li>
      <li>
        <a href="/privacy" style={linkStyle}>
          Privacy Policy
        </a>
      </li>
      <li>
        <a href="/refund-policy" style={linkStyle}>
          Refund Policy
        </a>
      </li>
    </ul>
  </div>
);
